/**
 * @architecture-header 1
 * @layer module
 * @domain authentication
 * @responsibility Present isolated organization-student daily login and one-time activation forms.
 * @state-owner React Hook Form and component-local screen state
 * @allowed-imports React Hook Form; Zod resolver; app environment; shared i18n; same-module APIs and schemas
 * @forbidden direct Supabase access; route registration; token storage; authorization decisions
 * @public-api integration-owner auth module export
 * @side-effects cookie-backed FastAPI credential requests; removes consumed QR URL fragments
 * @tests frontend/packages/workspace-web/src/modules/auth/components/OrganizationStudentAuthScreen.test.tsx
 * @security-boundary unauthenticated browser to FastAPI organization credential endpoints
 * @data-classification passwords and one-time codes stay form-local
 * @trust-model a successful FastAPI response proves only the managed session; workspace authorization remains backend-owned
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { VispoBrandLogo } from '../../../shared/components/BrandMarks';
import { CloseIcon } from '../../../shared/components/VispoIcon';
import { VispoInlineLoading } from '../../../shared/components/WorkspaceState';
import { useTranslator } from '../../../shared/model/use-workspace';
import {
  activateOrganizationCredential,
  loginWithOrganizationCredential,
  type OrganizationSession,
} from '../api/organization-credentials';
import {
  type OrganizationActivationValues,
  type OrganizationLoginValues,
  organizationActivationSchema,
  organizationLoginSchema,
  parseOrganizationCredentialFragment,
} from '../schemas/organization-credential';

export interface OrganizationStudentAuthScreenProps {
  initialMode?: 'login' | 'activate';
  legal: { consentRequired: boolean; privacyVersion: string; termsVersion: string };
  onAuthenticated(session: OrganizationSession): void | Promise<void>;
}

export function OrganizationStudentAuthScreen({
  initialMode = 'login',
  legal,
  onAuthenticated,
}: OrganizationStudentAuthScreenProps) {
  const environment = useWorkspaceEnvironment();
  const t = useTranslator();
  const [mode, setMode] = useState<'login' | 'activate'>(initialMode);
  const [error, setError] = useState<string | null>(null);
  const [wasFilledFromQr, setWasFilledFromQr] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const scannerVideoRef = useRef<HTMLVideoElement>(null);
  const login = useForm<OrganizationLoginValues>({
    resolver: zodResolver(organizationLoginSchema),
  });
  const activation = useForm<OrganizationActivationValues>({
    resolver: zodResolver(organizationActivationSchema),
    defaultValues: {
      acceptLegal: legal.consentRequired ? undefined : true,
      confirmPassword: '',
      loginCode: '',
      newPassword: '',
      oneTimeCode: '',
    },
  });

  useEffect(() => {
    const fragment = window.location.hash;
    if (!fragment) return;
    const parsed = parseOrganizationCredentialFragment(fragment);
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    if (!parsed) return;
    activation.reset({
      ...parsed,
      acceptLegal: legal.consentRequired ? undefined : true,
      confirmPassword: '',
      newPassword: '',
    });
    setWasFilledFromQr(true);
    setMode('activate');
  }, [activation, legal.consentRequired]);

  useEffect(() => {
    if (!scannerOpen) return;
    let stopped = false;
    let stopScanner: (() => void) | undefined;

    const startScanner = async () => {
      try {
        const { BrowserQRCodeReader } = await import('@zxing/browser');
        if (stopped || !scannerVideoRef.current) return;
        const reader = new BrowserQRCodeReader(undefined, { delayBetweenScanAttempts: 120 });
        const controls = await reader.decodeFromConstraints(
          { audio: false, video: { facingMode: { ideal: 'environment' } } },
          scannerVideoRef.current,
          (result, _scanError, scannerControls) => {
            if (!result) return;
            const scanned = parseScannedOrganizationCredential(result.getText());
            if (!scanned) {
              setScannerError(t('這不是有效的 Vispo 機構登入 QR Code'));
              return;
            }
            scannerControls.stop();
            activation.reset({
              ...activation.getValues(),
              ...scanned,
              acceptLegal: legal.consentRequired ? activation.getValues('acceptLegal') : true,
            });
            setWasFilledFromQr(true);
            setScannerError(null);
            setScannerOpen(false);
          },
        );
        stopScanner = controls.stop;
      } catch {
        if (!stopped) setScannerError(t('無法開啟相機，請允許相機權限或改用手動輸入。'));
      }
    };

    void startScanner();
    return () => {
      stopped = true;
      stopScanner?.();
    };
  }, [activation, legal.consentRequired, scannerOpen, t]);

  const reportError = (cause: unknown) => {
    setError(t(cause instanceof Error ? cause.message : '暫時無法完成登入，請稍後再試。'));
  };

  const submitLogin = async (values: OrganizationLoginValues) => {
    setError(null);
    try {
      await onAuthenticated(await loginWithOrganizationCredential(environment.apiClient, values));
    } catch (cause) {
      reportError(cause);
    }
  };

  const submitActivation = async (values: OrganizationActivationValues) => {
    setError(null);
    try {
      await onAuthenticated(
        await activateOrganizationCredential(environment.apiClient, values, legal),
      );
    } catch (cause) {
      reportError(cause);
    }
  };

  return (
    <main className="audience-login audience-login--student organization-login">
      <div aria-hidden="true" className="audience-login__orb audience-login__orb--one" />
      <div aria-hidden="true" className="audience-login__orb audience-login__orb--two" />
      <header className="audience-login__header">
        <a
          aria-label="回到 Vispo 首頁"
          className="audience-login__brand-link"
          href="https://vispo.app/"
        >
          <VispoBrandLogo className="audience-login__brand" size={34} variant="white" />
        </a>
        <Link className="audience-login__switch" to="/student/login">
          {t('返回學生登入')}
        </Link>
      </header>

      <section className="audience-login__card">
        <div className="audience-login__story">
          <span className="audience-login__eyebrow">ORGANIZATION SPACE</span>
          <h1 id="organization-login-title">{t('機構帳號')}</h1>
          <p className="audience-login__welcome">
            <strong>{t('回到你的學習空間。')}</strong>
            <span>{t('使用機構提供的帳號登入，班級、活動與學習記錄都會在這裡。')}</span>
          </p>
          <button
            className="audience-login__organization organization-login__mode"
            type="button"
            onClick={() => {
              setError(null);
              setMode(mode === 'login' ? 'activate' : 'login');
            }}
          >
            <span aria-hidden="true" className="audience-login__organization-icon">
              ⌁
            </span>
            <span>
              <strong>
                {mode === 'login' ? t('第一次登入或需要重設？') : t('已經設定密碼？')}
              </strong>
              <small>
                {mode === 'login' ? t('使用機構提供的啟用碼或重設碼') : t('返回每日機構帳號登入')}
              </small>
            </span>
            <span aria-hidden="true">→</span>
          </button>
        </div>

        <div className="audience-login__auth organization-login__auth">
          <h2>{mode === 'login' ? t('使用機構帳號登入') : t('啟用機構帳號')}</h2>
          {mode === 'login' ? (
            <form
              className="audience-login__form organization-login__form"
              onSubmit={login.handleSubmit(submitLogin)}
            >
              <label htmlFor="organization-login-code">
                <span>{t('機構帳號')}</span>
                <input
                  autoComplete="section-vispo-organization username"
                  id="organization-login-code"
                  {...login.register('loginCode')}
                />
                {login.formState.errors.loginCode ? (
                  <em>{t(login.formState.errors.loginCode.message ?? '')}</em>
                ) : null}
              </label>
              <label htmlFor="organization-password">
                <span>{t('密碼')}</span>
                <input
                  autoComplete="section-vispo-organization current-password"
                  id="organization-password"
                  type="password"
                  {...login.register('password')}
                />
                {login.formState.errors.password ? (
                  <em>{t(login.formState.errors.password.message ?? '')}</em>
                ) : null}
              </label>
              {error ? <p className="audience-login__error">{error}</p> : null}
              <button
                className="audience-login__submit"
                disabled={login.formState.isSubmitting}
                type="submit"
              >
                {login.formState.isSubmitting ? (
                  <VispoInlineLoading label={t('登入中…')} />
                ) : (
                  t('登入')
                )}
              </button>
            </form>
          ) : (
            <form
              className="audience-login__form organization-login__form organization-login__form--activation"
              onSubmit={activation.handleSubmit(submitActivation)}
            >
              <div aria-live="polite" className="organization-login__qr-help">
                <span>
                  {wasFilledFromQr
                    ? t('已從 QR Code 帶入機構帳號與一次性代碼，請設定新密碼。')
                    : t('掃描老師提供的 QR Code，可自動帶入機構帳號與一次性代碼；也可以手動輸入。')}
                </span>
                <button
                  aria-label={t('掃描 QR Code')}
                  className="organization-login__scan-button"
                  type="button"
                  onClick={() => {
                    setScannerError(null);
                    setScannerOpen(true);
                  }}
                >
                  <CameraIcon />
                  <span>{t('掃描 QR Code')}</span>
                </button>
              </div>
              <label htmlFor="activation-login-code">
                <span>{t('機構帳號')}</span>
                <input
                  autoComplete="section-vispo-organization username"
                  id="activation-login-code"
                  {...activation.register('loginCode')}
                />
              </label>
              <label htmlFor="activation-one-time-code">
                <span>{t('一次性代碼')}</span>
                <input
                  autoComplete="section-vispo-organization one-time-code"
                  id="activation-one-time-code"
                  {...activation.register('oneTimeCode')}
                />
              </label>
              <label htmlFor="activation-new-password">
                <span>{t('新密碼')}</span>
                <input
                  autoComplete="section-vispo-organization new-password"
                  id="activation-new-password"
                  type="password"
                  {...activation.register('newPassword')}
                />
              </label>
              <label htmlFor="activation-confirm-password">
                <span>{t('確認新密碼')}</span>
                <input
                  autoComplete="section-vispo-organization new-password"
                  id="activation-confirm-password"
                  type="password"
                  {...activation.register('confirmPassword')}
                />
              </label>
              {legal.consentRequired ? (
                <label className="organization-login__consent">
                  <input type="checkbox" {...activation.register('acceptLegal')} />{' '}
                  <span>{t('我已閱讀並同意服務條款與隱私權政策')}</span>
                </label>
              ) : null}
              {activation.formState.errors.loginCode ||
              activation.formState.errors.oneTimeCode ||
              activation.formState.errors.newPassword ||
              activation.formState.errors.confirmPassword ||
              activation.formState.errors.acceptLegal ? (
                <p className="audience-login__error">
                  {t(
                    activation.formState.errors.loginCode?.message ??
                      activation.formState.errors.oneTimeCode?.message ??
                      activation.formState.errors.newPassword?.message ??
                      activation.formState.errors.confirmPassword?.message ??
                      activation.formState.errors.acceptLegal?.message ??
                      '',
                  )}
                </p>
              ) : null}
              {error ? <p className="audience-login__error">{error}</p> : null}
              <button
                className="audience-login__submit"
                disabled={activation.formState.isSubmitting}
                type="submit"
              >
                {activation.formState.isSubmitting ? (
                  <VispoInlineLoading label={t('設定中…')} />
                ) : (
                  t('設定密碼並登入')
                )}
              </button>
              <p className="organization-login__help">{t('請找櫃檯或老師重發啟用碼')}</p>
            </form>
          )}
          <p className="audience-login__legal">
            {t('機構帳號由你的學校或補習班管理；需要協助時請聯絡老師或櫃檯。')}
          </p>
        </div>
      </section>
      {scannerOpen ? (
        <div className="organization-qr-scanner" role="presentation">
          <section
            aria-labelledby="organization-qr-scanner-title"
            aria-modal="true"
            className="organization-qr-scanner__dialog"
            role="dialog"
          >
            <header>
              <h2 id="organization-qr-scanner-title">{t('掃描 QR Code')}</h2>
              <button aria-label={t('關閉')} type="button" onClick={() => setScannerOpen(false)}>
                <CloseIcon />
              </button>
            </header>
            <div className="organization-qr-scanner__preview">
              <video autoPlay muted playsInline ref={scannerVideoRef} />
              <div aria-hidden="true" className="organization-qr-scanner__target" />
            </div>
            <p>{t('將老師提供的 QR Code 對準方框')}</p>
            {scannerError ? <p className="audience-login__error">{scannerError}</p> : null}
          </section>
        </div>
      ) : null}
    </main>
  );
}

export function parseScannedOrganizationCredential(value: string) {
  const hashIndex = value.indexOf('#');
  return parseOrganizationCredentialFragment(hashIndex >= 0 ? value.slice(hashIndex) : value);
}

function CameraIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M4 7.5h3l1.5-2h7l1.5 2h3v11H4z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  );
}
